import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { TrendingUp, Plus, Edit } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdsManagement = () => {
  const [users, setUsers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    totalSpend: '',
    campaignDuration: '',
    cpp: '',
    campaignStatus: 'Not Started',
    impressions: '',
    clicks: '',
    conversions: '',
    budget: ''
  });

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleEdit = async (user) => {
    setSelectedUser(user);
    try {
      const response = await axios.get(`${API}/ads/${user.principalId}`);
      if (response.data) {
        setFormData({
          totalSpend: response.data.totalSpend || '',
          campaignDuration: response.data.campaignDuration || '',
          cpp: response.data.cpp || '',
          campaignStatus: response.data.campaignStatus || 'Not Started',
          impressions: response.data.impressions || '',
          clicks: response.data.clicks || '',
          conversions: response.data.conversions || '',
          budget: response.data.budget || ''
        });
      }
    } catch (error) {
      // No existing data, use empty form
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/ads/${selectedUser.principalId}`, {
        userId: selectedUser.principalId,
        totalSpend: parseFloat(formData.totalSpend) || 0,
        campaignDuration: parseInt(formData.campaignDuration) || 0,
        cpp: parseFloat(formData.cpp) || 0,
        campaignStatus: formData.campaignStatus,
        impressions: parseInt(formData.impressions) || 0,
        clicks: parseInt(formData.clicks) || 0,
        conversions: parseInt(formData.conversions) || 0,
        budget: parseFloat(formData.budget) || 0
      });
      toast({ title: 'Success', description: 'Ads data updated successfully' });
      setDialogOpen(false);
      setSelectedUser(null);
      resetForm();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.error || 'Update failed', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({
      totalSpend: '',
      campaignDuration: '',
      cpp: '',
      campaignStatus: 'Not Started',
      impressions: '',
      clicks: '',
      conversions: '',
      budget: ''
    });
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-red-400 bg-clip-text text-transparent">Ads Tracking Management</h2>

      <div className="grid gap-4">
        {users.map(user => (
          <Card key={user.principalId} className="bg-white shadow-lg border-slate-200 hover:border-white/30 transition-all">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-slate-800">#{user.clientId} - {user.name}</p>
                  <p className="text-sm text-slate-500">{user.email}</p>
                  <p className="text-xs text-slate-400 mt-1">Plan: {user.activePlan || 'None'}</p>
                </div>
                <Button size="sm" onClick={() => handleEdit(user)} className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Ads Data
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-orange-400">Edit Ads Tracking Data</DialogTitle>
            {selectedUser && (
              <p className="text-sm text-slate-500">#{selectedUser.clientId} - {selectedUser.name}</p>
            )}
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-600">Total Spend (₹)</Label>
                <Input
                  type="number"
                  value={formData.totalSpend}
                  onChange={(e) => setFormData({ ...formData, totalSpend: e.target.value })}
                  placeholder="5000"
                  className="bg-slate-50 border-slate-200 text-slate-800"
                />
              </div>
              <div>
                <Label className="text-slate-600">Campaign Duration (Days)</Label>
                <Input
                  type="number"
                  value={formData.campaignDuration}
                  onChange={(e) => setFormData({ ...formData, campaignDuration: e.target.value })}
                  placeholder="30"
                  className="bg-slate-50 border-slate-200 text-slate-800"
                />
              </div>
              <div>
                <Label className="text-slate-600">CPP (₹)</Label>
                <Input
                  type="number"
                  value={formData.cpp}
                  onChange={(e) => setFormData({ ...formData, cpp: e.target.value })}
                  placeholder="150"
                  className="bg-slate-50 border-slate-200 text-slate-800"
                />
              </div>
              <div>
                <Label className="text-slate-600">Campaign Status</Label>
                <Select value={formData.campaignStatus} onValueChange={(value) => setFormData({ ...formData, campaignStatus: value })}>
                  <SelectTrigger className="bg-slate-50 border-slate-200 text-slate-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Not Started">Not Started</SelectItem>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Paused">Paused</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-600">Impressions</Label>
                <Input
                  type="number"
                  value={formData.impressions}
                  onChange={(e) => setFormData({ ...formData, impressions: e.target.value })}
                  placeholder="10000"
                  className="bg-slate-50 border-slate-200 text-slate-800"
                />
              </div>
              <div>
                <Label className="text-slate-600">Clicks</Label>
                <Input
                  type="number"
                  value={formData.clicks}
                  onChange={(e) => setFormData({ ...formData, clicks: e.target.value })}
                  placeholder="500"
                  className="bg-slate-50 border-slate-200 text-slate-800"
                />
              </div>
              <div>
                <Label className="text-slate-600">Conversions</Label>
                <Input
                  type="number"
                  value={formData.conversions}
                  onChange={(e) => setFormData({ ...formData, conversions: e.target.value })}
                  placeholder="25"
                  className="bg-slate-50 border-slate-200 text-slate-800"
                />
              </div>
              <div>
                <Label className="text-slate-600">Budget (₹)</Label>
                <Input
                  type="number"
                  value={formData.budget}
                  onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                  placeholder="10000"
                  className="bg-slate-50 border-slate-200 text-slate-800"
                />
              </div>
            </div>
            <Button type="submit" className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600">
              Update Ads Data
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdsManagement;